import { parseDateValue, useFormatDate } from '@ben/common/date';
import CloseLineIcon from '@ben/icons/close-line';
import Checkbox from '@ben/orion/checkbox';
import DateInput from '@ben/orion/date-input';
import IconButton from '@ben/orion/icon-button';
import Input from '@ben/orion/input';
import Select from '@ben/orion/select';
import { TypographyVariant } from '@ben/orion/typography';
import { useCallback } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { START_TRIGGER_OPTIONS } from './constant';
import { FormContainer, Spacer, StyledTypography } from './styles';
import type { RestrictionFormPropsWithSetValue } from './types';

const ExclusionPeriodForm = ({
  index,
  control,
  setValue,
  restrictionSetPath,
  onRemove,
}: RestrictionFormPropsWithSetValue) => {
  const formatDate = useFormatDate();
  const basePath = `${restrictionSetPath}.exclusionPeriods.${index}`;

  const startTrigger = useWatch({
    control,
    name: `${basePath}.startTrigger`,
  }) as string | undefined;

  const startTriggerSpecificDate = useWatch({
    control,
    name: `${basePath}.startTriggerSpecificDate`,
  }) as string | undefined;

  const handleDateChange = useCallback(
    (value: Date | string | null) => {
      const date = typeof value === 'string' ? parseDateValue(value) : value;
      setValue(
        `${basePath}.startTriggerSpecificDate`,
        date ? formatDate(date, { formatString: 'yyyy-MM-dd' }) : undefined
      );
    },
    [setValue, basePath, formatDate]
  );

  return (
    <FormContainer>
      <FormRow>
        <StyledTypography variant={TypographyVariant.bodyMd1}>
          #{index + 1}
        </StyledTypography>
        <StyledTypography variant={TypographyVariant.bodyMd1}>
          Start trigger:
        </StyledTypography>
        <Controller
          control={control}
          name={`${basePath}.startTrigger`}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              value={field.value}
              data={START_TRIGGER_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              onChange={field.onChange}
              width="170px"
            />
          )}
        />

        {startTrigger === 'specific_date' && (
          <DateInput
            label="Date"
            value={
              startTriggerSpecificDate
                ? parseDateValue(startTriggerSpecificDate)
                : null
            }
            onChange={handleDateChange}
            width="150px"
            showLabel={false}
          />
        )}

        <StyledTypography variant={TypographyVariant.bodyMd1}>
          Duration days:
        </StyledTypography>
        <Controller
          control={control}
          name={`${basePath}.durationDays`}
          render={({ field }) => (
            <Input
              name="Duration days"
              type="number"
              showLabel={false}
              value={field.value ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                field.onChange(value === '' ? null : Number(value));
              }}
              width="50px"
              min={0}
            />
          )}
        />
        <Spacer />
        <IconButton
          icon={CloseLineIcon}
          size="sm"
          onClick={onRemove}
          aria-label="Remove restriction"
          variant="action"
        />
      </FormRow>

      <CheckboxRow>
        <Controller
          control={control}
          name={`${basePath}.ignoreRestrictions`}
          render={({ field }) => (
            <Checkbox
              label="Ignore restrictions"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={control}
          name={`${basePath}.ignoreWindows`}
          render={({ field }) => (
            <Checkbox
              label="Ignore windows"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </CheckboxRow>
    </FormContainer>
  );
};

export default ExclusionPeriodForm;

const FormRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    width: 100%;
    margin-top: ${theme.spacing(1)};

    &:first-child {
      margin-top: 0;
    }
  `}
`;

const CheckboxRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(3)};
    margin-top: ${theme.spacing(1.5)};
  `}
`;
